import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, type Board, type SearchFilters, type SearchHit } from "../api.ts";
import { tokens as design, useTheme } from "../glassTheme.tsx";
import { GlassFloat } from "../components/glass/Glass.tsx";
import { IconSearch } from "../components/glass/navIcons.tsx";
import { FlagHitRow, ItemHitRow, TaskHitRow } from "../components/search/HitRows.tsx";
import { idFromQuery, queryTokens } from "../searchHighlight.ts";

export interface SearchState { q: string; filters: SearchFilters }
export const emptySearch: SearchState = { q: "", filters: { value: [], tier: [], state: [] } };

const DEBOUNCE_MS = 150;

const TIERS = [{ key: "must", label: "Must" }, { key: "should", label: "Should" }, { key: "nice", label: "Nice" }];
const STATES = [
  { key: "open", label: "Open" }, { key: "waiting", label: "Waiting" }, { key: "blocked", label: "Blocked" },
  { key: "parked", label: "Parked" }, { key: "done", label: "Done" },
];

type Group = keyof SearchFilters;

/**
 * Search. One input, focused on entry; everything on the board is in reach —
 * parked and done included, because nothing here is ever deleted and this is
 * how a parked thing comes back. What matches is decided by the server, in
 * one place, the same place chat asks.
 *
 * The query and the filters live in App, not here, so they outlast the
 * view; the task sheet opens over this component rather than replacing it,
 * which is what keeps the scroll where it was when the sheet closes.
 *
 * Deliberately absent: how many things matched. A result is a thing to open,
 * not a figure.
 */
export function Search({
  board, state, onState, onOpen, onOpenIdeas,
}: {
  board: Board; state: SearchState; onState: (s: SearchState) => void;
  onOpen: (id: string) => void; onOpenIdeas: () => void;
}) {
  const { colors } = useTheme();
  const { q, filters } = state;
  const inputRef = useRef<HTMLInputElement>(null);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [asked, setAsked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [openGroup, setOpenGroup] = useState<Group | null>(null);

  // Layout effect, not effect: the keyboard only opens if focus lands inside
  // the same tap that brought us here.
  useLayoutEffect(() => { inputRef.current?.focus(); }, []);

  const filterKey = JSON.stringify(filters);

  // A new question starts at the top. Deliberately not tied to `board`: the
  // refresh that follows closing the task sheet must leave the scroll alone.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [q, filterKey]);

  useEffect(() => {
    if (!q.trim()) { setHits([]); setAsked(false); setFailed(false); return; }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const got = await api.search(q, filters, ctl.signal);
        setHits(got); setAsked(true); setFailed(false);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setFailed(true);
      }
    }, DEBOUNCE_MS);
    return () => { clearTimeout(t); ctl.abort(); };
    // `board` is here so a change made in the task sheet (parked, done,
    // renamed) is reflected the moment the sheet closes.
  }, [q, filterKey, board]);

  const words = useMemo(() => queryTokens(q), [q]);
  const wantedId = useMemo(() => idFromQuery(q), [q]);

  const toggle = (g: Group, key: string) => {
    const has = filters[g].includes(key);
    onState({ q, filters: { ...filters, [g]: has ? filters[g].filter((k) => k !== key) : [...filters[g], key] } });
  };
  const anyFilter = filters.value.length + filters.tier.length + filters.state.length > 0;

  const open = (id: string) => { inputRef.current?.blur(); onOpen(id); };

  const groups: { key: Group; label: string; options: { key: string; label: string; dot?: string }[] }[] = [
    { key: "value", label: "Value", options: board.values.map((v) => ({
      key: v.key, label: v.name, dot: (colors.tint as Record<string, string>)[v.key],
    })) },
    { key: "tier", label: "Tier", options: TIERS },
    { key: "state", label: "State", options: STATES },
  ];
  const active = groups.find((g) => g.key === openGroup);

  const chip = (on: boolean): React.CSSProperties => ({
    flex: "none", minHeight: design.size.touchTargetMin, padding: "0 16px", borderRadius: 999, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 8,
    fontFamily: design.font.sans, fontSize: design.type.segment.size,
    border: `1px solid ${on ? colors.accent : colors.hair2}`,
    background: on ? colors.accentSoft : "transparent", color: on ? colors.ink : colors.ink2,
  });
  const scroller: React.CSSProperties = {
    display: "flex", gap: 8, overflowX: "auto", margin: "0 -16px", padding: "0 16px", scrollbarWidth: "none",
  };

  return (
    <div style={{ padding: "12px 16px 0" }}>
      {/* Stays put while the results scroll under it. */}
      <div style={{ position: "sticky", top: 8, zIndex: 5 }}>
        <GlassFloat style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 6px 0 18px", minHeight: 52 }}>
          <span style={{ color: colors.ink2, display: "grid", flex: "none" }}><IconSearch size={20} /></span>
          <input
            ref={inputRef} autoFocus value={q}
            onChange={(e) => onState({ q: e.target.value, filters })}
            type="text" inputMode="search" enterKeyHint="search" placeholder="Search the board"
            aria-label="Search the board" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
            style={{
              flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
              color: colors.ink, fontFamily: design.font.sans, fontSize: 16, padding: "14px 0",
            }}
          />
          {q && (
            <button
              onClick={() => { onState({ q: "", filters }); inputRef.current?.focus(); }}
              aria-label="Clear"
              style={{
                flex: "none", width: design.size.touchTargetMin, height: design.size.touchTargetMin, border: "none",
                background: "transparent", color: colors.ink2, fontSize: 20, lineHeight: 1, cursor: "pointer",
              }}
            >×</button>
          )}
        </GlassFloat>
      </div>

      <div style={{ ...scroller, marginTop: 12 }}>
        {groups.map((g) => {
          const chosen = filters[g.key];
          const label = chosen.length === 0 ? g.label
            : `${g.label} · ${chosen.map((k) => g.options.find((o) => o.key === k)?.label ?? k).join(", ")}`;
          return (
            <button
              key={g.key} onClick={() => setOpenGroup(openGroup === g.key ? null : g.key)}
              aria-expanded={openGroup === g.key}
              style={{ ...chip(chosen.length > 0 || openGroup === g.key), maxWidth: 220 }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
            </button>
          );
        })}
        {anyFilter && (
          <button
            onClick={() => onState({ q, filters: emptySearch.filters })}
            style={{ ...chip(false), border: "none", textDecoration: "underline" }}
          >Clear</button>
        )}
      </div>

      {active && (
        <div style={{ ...scroller, marginTop: 8 }}>
          {active.options.map((o) => {
            const on = filters[active.key].includes(o.key);
            return (
              <button key={o.key} onClick={() => toggle(active.key, o.key)} aria-pressed={on} style={chip(on)}>
                {o.dot && <span style={{ width: design.size.valueDot, height: design.size.valueDot, borderRadius: "50%", background: o.dot }} />}
                {o.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {hits.length > 0 && (
          <div style={{ background: colors.sheet, borderRadius: design.radius.card, padding: "0 12px" }}>
            {hits.map((h) =>
              h.kind === "task" ? (
                <TaskHitRow key={`t${h.id}`} task={h} tokens={words} idMatch={h.id === wantedId} onOpen={() => open(h.id)} />
              ) : h.kind === "flag" ? (
                <FlagHitRow key={`f${h.id}`} flag={h} tokens={words} idMatch={h.id === wantedId} onOpenTask={open} />
              ) : (
                <ItemHitRow
                  key={`i${h.id}`} item={h} tokens={words}
                  onOpen={() => (h.promoted_task_id ? open(h.promoted_task_id) : onOpenIdeas())}
                />
              ),
            )}
          </div>
        )}

        {hits.length === 0 && (
          <p style={{ margin: 0, padding: "10px 4px", fontSize: 13, color: colors.ink2 }}>
            {failed ? "Couldn’t search just now."
              : !q.trim() ? "Everything on the board, parked and done included."
              : asked ? "Nothing found." : ""}
          </p>
        )}
      </div>
    </div>
  );
}
